import Analytics from 'analytics';
import googleTagManager from '@analytics/google-tag-manager';
import { getGTMID } from './constants/googleAnalytics';
import GTMDimensions, { Dimension14Mapper } from './constants/gtmDimensions';
import { StringHelpers } from './stringHelpers';
import { CurrencyHelpers } from './currencyHelpers';
import isEqual from 'lodash.isequal';
import { Money, Product, Variant } from 'shared/types/product';
import { Account } from 'shared/types/account';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { SelectedAttributes } from 'components/commercetools-ui/organisms/product/product-details/types';
import { LineItem } from 'shared/types/cart';
import useLocale from './hooks/useLocale';

interface Datalayer {
  breadcrumb: string;
  event: string;
  step?: number;
  option?: string;
  store?: string;
  dimension18?: string;
}

/**
 * @class DataLayerHelpers
 * @classdesc DataLayerHelpers class to set data layer
 * @param {string} locale - current locale
 * @param {object} cartData - object to set cart data
 * @param {string} userEmail - user email (empty string if not logged in)
 * @param {string} userId - user id (empty string if not logged in)
 * @param {string} salutation - user salutation (empty string if not logged in)
 * @param {string} paymentMethod - payment method
 */
export class DataLayerHelpers {
  public analytics: any;
  private cartData: any;
  userEmail: string;
  userId: string;
  locale: string;
  salutation: string;
  paymentMethod: string;
  brandstakId: string;
  orderUserEmail: string;
  siteURL: string;

  constructor(
    siteURL: string,
    locale: string,
    cartData?: Record<string, any>,
    userEmail?: string,
    userId?: string,
    salutation?: string,
    paymentMethod?: string,
    brandstakId?: string,
    orderUserEmail?: string,
  ) {
    this.siteURL = siteURL;
    this.cartData = cartData;
    this.locale = locale;
    this.userEmail = userEmail || '';
    this.userId = userId || '';
    this.salutation = salutation || '';
    this.paymentMethod = paymentMethod || '';
    this.brandstakId = brandstakId || '';
    this.orderUserEmail = orderUserEmail || '';
  }
  private setStoreLocation = (shippingAddress: any) => {
    if (shippingAddress) {
      return `${shippingAddress.streetName}, ${shippingAddress.postalCode}, ${shippingAddress.city} ${shippingAddress.country}`;
    }
    return '';
  };

  private transformPurchaseConfirmation = (dataLayer: Datalayer) => {
    const cartData = this.cartData;
    const { breadcrumb, event } = dataLayer;
    const shippingAddress = cartData?.shippingAddress;
    const cartDiscount = cartData?.discountOnTotalPrice || undefined;

    const transformedData = {
      dimension4: cartData?.shippingInfo?.description || '',
      dimension5: cartData?.payments?.[0]?.paymentMethod || '',
      dimension16: this.setStoreLocation(shippingAddress),
      dimension18: dataLayer.dimension18,
      metric2: this.formatCurrency(cartDiscount),
      metric3: 0,
      metric4: 0,
      breadcrumb,
      event,
      ecommerce: {
        currencyCode: this.cartData?.sum?.currencyCode || '',
        purchase: {
          actionField: {
            id: cartData?.orderId || '',
            affiliation: this.userEmail ? 'registered' : 'guest',
            revenue: this.formatCurrency(cartData?.sum),
            tax: this.formatCurrency(cartData?.taxed?.amount),
            shipping: this.formatCurrency(cartData?.shippingInfo?.price),
            coupon: cartData?.discountCodes?.map((discount: any) => discount.code).join(', '),
            action: 'purchase',
          },
          products: cartData.lineItems.map(this.purchaseLineItems),
        },
      },
      customerEmail: this.orderUserEmail,
      brandstakId: this.brandstakId,
    };

    return transformedData;
  };

  private createPurchaseProduct = (lineItem: Record<string, any>) => {
    return {
      id: lineItem.variant.sku,
      name: lineItem.name,
      price: this.formatCurrency(lineItem.price),
      priceValue: parseFloat(this.formatCurrency(lineItem.price)),
      brand: lineItem?.variant?.attributes?.brand || '',
      category: lineItem?.categoryName || '',
      variant: lineItem.productKey || '',
      quantity: String(lineItem.count) || '',
      quantityValue: lineItem.count || 0,
      metric1: this.formatCurrency(lineItem?.variant?.discountedPrice),
    };
  };

  private purchaseLineItems = (lineItem: Record<string, any>) => {
    if (lineItem.variant && lineItem.variant.attributes) {
      const optionsOrder = Object.keys(lineItem.variant.attributes);
      const attributes = lineItem.variant.attributes;

      let createPurchaseProduct: Record<string, any> = this.createPurchaseProduct(lineItem);
      createPurchaseProduct = this.addDimensionsToProduct(createPurchaseProduct, optionsOrder, attributes, 'purchase');

      return createPurchaseProduct;
    }
  };

  private transformAddToCartDataLayer = () => {
    const dimensionData = this.addDimensionsToProduct(
      this.cartData,
      Object.keys(this.cartData.variant.attributes),
      this.cartData.variant.attributes,
      'addToCart',
    );
    const cartDiscount = this.cartData?.discountOnTotalPrice || undefined;
    const transformedData: Record<string, any> = {
      event: 'addToCart',
      ecommerce: {
        currencyCode: this.cartData.variant?.price?.currencyCode || '',
        add: {
          products: [
            {
              id: this.cartData?.variant?.sku || '',
              name: this.cartData?.name || '',
              price: this.formatCurrency(this.cartData.variant.price),
              brand: this.cartData.variant.attributes.brand || '',
              variant: this.cartData?.productId || '',
              category: this.cartData?.categoryName || '',
              metric1: this.formatCurrency(this.cartData.variant.discountedPrice),
              metric2: this.formatCurrency(cartDiscount),
              ...dimensionData,
            },
          ],
        },
      },
    };

    return transformedData;
  };

  private transformRemoveCartItemDataLayer = () => {
    const transformedData: Record<string, any> = {
      event: 'removeFromCart',
      ecommerce: {
        remove: {
          products: [
            {
              brand: this.cartData.brand || '',
              id: this.cartData?.sku || '',
              name: this.cartData?.name || '',
              price: this.cartData.price,
              variant: this.cartData?.productId || '',
              quantity: this.cartData?.quantity,
              category: this.cartData?.category || '',
            },
          ],
        },
      },
    };

    return transformedData;
  };

  private transformCartObjToDataLayer = (dataLayerData: Datalayer) => {
    const { breadcrumb, event, step, option } = dataLayerData;
    const cartItems = this.cartData?.lineItems || [];
    const cartDiscount = this.cartData?.discountOnTotalPrice || undefined;
    const deliveryOptionId = this.cartData?.custom?.deliveryOption;
    const transformedData = {
      metric2: this.formatCurrency(cartDiscount),
      dimension4:
        (dataLayerData.step === 3 || dataLayerData.step === 4) && deliveryOptionId === 'HomeDelivery'
          ? this.cartData.shippingInfo?.name || ''
          : '',
      dimension5: this.paymentMethod,
      dimension16: deliveryOptionId === 'StoreCollection' ? dataLayerData.store : '',
      shippingSelection: deliveryOptionId === 'StoreCollection' ? 'delivertostore' : 'homedelivery',
      userEmail: this.userEmail,
      breadcrumb,
      event,
      ecommerce: {
        actionField: {
          step,
          option,
        },
        checkout: {
          products: cartItems.map(this.transformLineItemToProduct),
        },
      },
    };

    return transformedData;
  };
  private transformCartObjToEmarsysLayer = () => {
    const cartItems = this.cartData?.lineItems || [];

    const basketItems = <any>[];

    cartItems?.map((lineItem: Record<string, any>) => {
      const eachItemData = {
        item: lineItem?.variant?.sku || '',
        title: lineItem?.name || '',
        price: this.formatCurrency(lineItem?.price),
        quantity: lineItem?.count || '',
        brand: lineItem?.variant?.attributes?.brand || '',
        variant: lineItem?.productKey || '',
        category: (lineItem?.categoryName && lineItem?.categoryName?.replace(/ \/\s*/, '/')) || '',
      };
      basketItems?.push(eachItemData);
    });

    return basketItems;
  };

  private createProductObject = (lineItem: Record<string, any>) => {
    const attributes = lineItem.variant.attributes;
    let dimension15 = '';
    if ('leg' in attributes) {
      dimension15 = attributes.leg;
    } else if ('width' in attributes) {
      dimension15 = attributes.width;
    } else if ('bounce' in attributes) {
      dimension15 = attributes.bounce;
    }

    return {
      name: lineItem.name || '',
      id: lineItem.variant.sku || '',
      metric1: this.formatCurrency(lineItem.discountedPrice),
      price: this.formatCurrency(lineItem.price),
      brand: attributes.brand || '',
      category: lineItem?.categoryName || '',
      variant: lineItem.productKey || '',
      quantity: lineItem.count,
      dimension15,
    };
  };

  private formatCurrency = (price: Record<string, any>) => {
    if (price === undefined) return '0.00';
    return (price.centAmount / 100).toFixed(2);
  };

  private addPriceWithTaxed = (value1: Money, value2: Money) => {
    const totalPrice = CurrencyHelpers.addCurrency(value1, value2);
    return this.formatCurrency(totalPrice);
  };

  private addDimensionsToProduct = (
    product: Record<string, any>,
    optionsOrder: string[],
    attributes: Record<string, any>,
    event: string,
  ) => {
    let productDimensions = event === 'addToCart' ? {} : product;
    const dimensions = Array.from(new Set([...optionsOrder, ...Object.keys(GTMDimensions)]));
    if (Array.isArray(optionsOrder)) {
      dimensions.forEach((dimensionName) => {
        const index = GTMDimensions[dimensionName.toLowerCase()];
        if (index !== undefined) {
          const key = `dimension${index}`;
          if (!(key === 'dimension4' || key === 'dimension5' || key === 'dimension16')) {
            productDimensions[key] = attributes[StringHelpers.camelCaseString(dimensionName)] || '';
          }
        }
      });
    }
    let dimension15 = '';
    if ('leg' in attributes) {
      dimension15 = attributes.leg;
    } else if ('width' in attributes) {
      dimension15 = attributes.width;
    } else if ('bounce' in attributes) {
      dimension15 = attributes.bounce;
    }

    const dimension6 = attributes.size ?? attributes.shoesize ?? attributes.waist ?? '';

    productDimensions['dimension1'] = this.userId;
    productDimensions['dimension2'] = this.userEmail ? 'login-yes' : 'login-no';
    productDimensions['dimension3'] = 'loyalty-no';
    if (dimension6) productDimensions['dimension6'] = dimension6;
    productDimensions['dimension13'] = this.salutation;
    productDimensions['dimension14'] = event === 'addToCart' ? 'wishlist' : event;
    if (dimension15) productDimensions['dimension15'] = dimension15 ?? '';
    /*
    productDimensions['dimension21'] = '';
    productDimensions['dimension21'] = '';
    productDimensions['dimension20'] = '';
    productDimensions['dimension21'] = '';
    */
    return productDimensions;
  };

  private transformLineItemToProduct = (lineItem: Record<string, any>) => {
    if (lineItem.variant && lineItem.variant.attributes) {
      const optionsOrder = Object.keys(lineItem.variant.attributes);
      const attributes = lineItem.variant.attributes;

      let product: Record<string, any> = this.createProductObject(lineItem);

      product = this.addDimensionsToProduct(product, optionsOrder, attributes, 'checkout');

      return product;
    }
  };

  public initAnalytics = () => {
    const currentUrl = document.location.origin;
    this.analytics = Analytics({
      app: 'agstore',
      plugins: [
        googleTagManager({
          containerId: getGTMID(currentUrl),
        }),
      ],
    });
    this.analytics.page();
  };
  public pushImpressionsToDataLayer(products: any, breadcrumb: any, currency: string) {
    const breadcrumbString = breadcrumb
      ?.filter((item: any) => item?.name !== 'Home')
      .map((item: any) => item?.name)
      .join(' / ');
    const impression = products?.flatMap((product: any, index: number) => {
      if (!product?.variants || product.variants.length === 0) return [];

      return product?.variants?.map((variant: Variant) => ({
        id: variant?.sku,
        name: product?.name?.['en-GB'] || '',
        price: '00.00',
        brand: product?.attributes?.brand || '',
        variant: product?.key?.split('_')?.[0],
        list: breadcrumb?.at(-1)?.name || '',
        category: breadcrumb?.[breadcrumb.length - 1]?.href || '',
        position: index,
      }));
    });
    this.initAnalytics();

    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        const payload = {
          event: 'impressions',
          ecommerce: {
            currencyCode: currency,
            impression,
          },
          breadcrumb: breadcrumbString,
        };

        window.dataLayer.push(payload);
      }
    });
  }

  public pushProductDetailToDataLayer(
    product: Product,
    variant: Variant,
    currency: string,
    selectedVariant: StringHelpers,
  ) {
    this.initAnalytics();
    const breadcrumbStringData = (product?.breadCrumb as any)
      .map((item: any) => item?.name)
      .slice(1)
      .join('/');
    const subcat = (product?.breadCrumb as any).map((item: any) => item?.name)?.[1];
    const priceData = variant?.attributes?.standalonePrice && JSON.parse(variant?.attributes?.standalonePrice);
    const cost = priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
      ? priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
      : '00.00';

    let productPayload: any;

    if (selectedVariant === null) {
      productPayload = {
        name: product?.name,
        id: product?.variants?.[0]?.sku,
        variant: product?.productId?.split('_')?.[0],
        brand: variant?.attributes?.brand,
        category: breadcrumbStringData,
        price: (cost / 100).toFixed(2),
        metric1: '0.00',
      };
    } else {
      productPayload = {
        name: product?.name,
        id: variant?.sku,
        variant: product?.productId?.split('_')?.[0],
        brand: variant?.attributes?.brand,
        category: breadcrumbStringData,
        price: (cost / 100).toFixed(2),
        metric1: '0.00',
      };
    }

    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window?.dataLayer) {
        window.dataLayer.push({
          event: 'productDetail',
          breadrumb: breadcrumbStringData,
          dimension14: 'product',
          ecommerce: {
            currencyCode: currency,
            detail: {
              actionField: {
                list: subcat,
              },
              products: [productPayload],
            },
          },
        });
      }
    });

    const ForTesting = {
      event: 'productDetail',
      breadrumb: breadcrumbStringData,
      dimension14: 'product',
      ecommerce: {
        currencyCode: currency,
        detail: {
          actionField: {
            list: subcat,
          },
          products: [productPayload],
        },
      },
    };

    //console for checking
    console.log('productDetailEvent', JSON.stringify(ForTesting));
  }

  public pushProductViewUpdateToDataLayer(
    product: Product,
    variant: Record<string, any>,
    currency: string,
    selectedAttributes: SelectedAttributes,
    payloadAttributes: any,
    customFitVariant: any,
    isGolfClubProducts: any,
  ) {
    if (!variant) return;

    this.initAnalytics();
    const breadcrumbStringData = (product?.breadCrumb as any)
      .map((item: any) => item?.name)
      .slice(1)
      .join('/');

    const priceData = variant?.attributes?.standalonePrice && JSON.parse(variant?.attributes?.standalonePrice);
    const cost = priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
      ? priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
      : '00.00';

    const subcat = (product?.breadCrumb as any).map((item: any) => item?.name)?.[1];
    let payload: any;
    if (customFitVariant || isGolfClubProducts) {
      const dimensions = {};
      const attributesMap = payloadAttributes.reduce((acc: any, attr: any) => {
        acc[attr.name] = attr.value;
        return acc;
      }, {});

      payload = {
        event: 'productViewUpdate',
        ecommerce: {
          currencyCode: currency,
          detail: {
            actionField: {
              list: subcat,
            },
            products: [
              {
                id: variant?.sku,
                name: product?.name,
                price: (cost / 100).toFixed(2),
                brand: variant?.attributes?.brand,
                variant: product?.productId,
                category: breadcrumbStringData,
                ...attributesMap,
                metric1: '0.00',
              },
            ],
          },
        },
      };
    } else {
      const validAttributes = Object.fromEntries(
        Object.entries(selectedAttributes).filter(([_, value]) => value.trim() !== ''),
      );

      payload = {
        event: 'productViewUpdate',
        ecommerce: {
          currencyCode: 'GBP',
          detail: {
            actionField: {
              list: 'Golf Clothing',
            },
            products: [
              {
                id: variant.sku,
                name: product?.name,
                price: (cost / 100).toFixed(2),
                brand: variant?.attributes?.brand,
                variant: product?.productId,
                category: breadcrumbStringData,
                ...validAttributes,
                metric1: '0.00',
              },
            ],
          },
        },
      };
    }

    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window?.dataLayer) {
        window.dataLayer.push({
          event: 'productViewUpdate',
          payload,
        });
      }
    });
  }

  public pushProductCartContent(lineItems: LineItem[] | undefined) {
    this.initAnalytics();

    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    const cartContentData = (lineItems || []).map((i: any) => ({
      brand: i?.variant?.attributes?.brand || '',
      category: i?.categoryName || '',
      item: i?.variant?.sku || '',
      price: (i?.price.centAmount / 100).toFixed(2) || '',
      quantity: i?.count || '',
      title: i?.name || '',
      variant: i?.productKey || '',
    }));

    const pushToDataLayer = () => {
      console.log('[cartContent] Pushing to dataLayer:', cartContentData);
      window.dataLayer.push({
        event: 'cartContent',
        cartContent: cartContentData,
      });
    };

    let pushed = false;

    // Primary trigger: analytics 'pageEnd' event
    if (this.analytics?.once) {
      this.analytics.once('pageEnd', () => {
        if (!pushed) {
          pushed = true;
          pushToDataLayer();
        }
      });
    }

    // Fallback trigger: 3-second timeout
    setTimeout(() => {
      if (!pushed) {
        pushed = true;
        pushToDataLayer();
      }
    }, 2000);
  }

  public pushProductAddToCartContent(lineItem: LineItem, currency: string) {
    this.initAnalytics();

    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window?.dataLayer) {
        window.dataLayer = window.dataLayer || [];

        const priceData =
          lineItem?.variant?.attributes?.standalonePrice && JSON.parse(lineItem?.variant?.attributes?.standalonePrice);
        const cost = priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
          ? priceData?.[currency === 'GBP' ? 'en-GB' : 'en-EU']?.value?.centAmount
          : '00.00';
        const products = {
          brand: lineItem?.variant?.attributes?.brand,
          category: lineItem?.categoryName && lineItem?.categoryName?.replace(/ \/\s*/, '/'),
          id: lineItem?.variant?.sku,
          name: lineItem?.name,
          price: (cost / 100).toFixed(2) || '',
          variant: lineItem?.productKey,
          quantity: lineItem.count,
          dimension6: lineItem?.variant?.attributes?.colour || '',
          dimension7: lineItem?.variant?.attributes?.size || '',
          dimension8: lineItem?.variant?.attributes?.hand || '',
          dimension9: lineItem?.variant?.attributes?.loft || '',
          dimension10: lineItem?.variant?.attributes?.shaft || '',
          dimension11: lineItem?.variant?.attributes?.stiff || '',
          dimension12: lineItem?.variant?.attributes?.gender || '',
          dimension15: '',
          dimension16: '',
          metric1: '0.00',
          metric2: '0.00',
        };

        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode: currency,
            add: {
              products: [products],
            },
          },
        });
        console.log(
          'addToCartEvent',
          JSON.stringify({
            ecommerce: {
              currencyCode: currency,
              add: {
                products: [products],
              },
            },
          }),
        );
      }
    });
  }

  // public pushCartContentToDataLayerFunction(
  //   product: Product,
  //   path: string,
  //   variant: Record<string, any>,
  //   currency: string,
  //   selectedAttributes: SelectedAttributes,
  // ) {
  //   if (!product) return;

  //   this.initAnalytics();

  //   this.analytics.once('pageEnd', () => {
  //     if (typeof window !== 'undefined' && window?.dataLayer) {
  //       window.dataLayer = window?.dataLayer || [];
  //       window.dataLayer.push({
  //         event: 'cartContent',
  //         cartContent: [
  //           {
  //             brand: variant?.attributes?.brand,
  //             category: path,
  //             item: variant?.id,
  //             price: variant?.price || '',
  //             quantity: 1,
  //             title: product?.name,
  //             variant: variant?.sku,
  //           },
  //         ],
  //       });
  //     }
  //   });
  // }

  public setPurchaseDataLayer = (dataLayer: Datalayer) => {
    this.initAnalytics();
    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(this.transformPurchaseConfirmation(dataLayer));
      }
    });
  };

  public setAddToCartDataLayer() {
    const cartDimensions = this.transformAddToCartDataLayer();

    const isDataFound = window.dataLayer.some(
      (data: any) =>
        data.ecommerce &&
        data.ecommerce.add &&
        data.ecommerce.add.products &&
        data.ecommerce.add.products.some(
          (product: any) => product.variant === cartDimensions.ecommerce.add.products[0].variant,
        ),
    );

    if (isDataFound) return;

    let addToCartEvent = window.dataLayer.find((data: any) => data.event === 'addToCart');

    if (addToCartEvent) {
      addToCartEvent.ecommerce.add.products.push(cartDimensions.ecommerce.add.products[0]);
    } else {
      this.initAnalytics();
      this.analytics.once('pageEnd', () => {
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push(cartDimensions);
        }
      });
    }
  }

  public setRemoveCartItemDataLayer() {
    const removedItem = this.transformRemoveCartItemDataLayer();

    /*
    const isDataFound = window.dataLayer.some(
      (data: any) =>
        data.ecommerce &&
        data.ecommerce.remove &&
        data.ecommerce.remove.products &&
        data.ecommerce.remove.products.some(
          (product: any) => product.variant === removedItem.ecommerce.remove.products[0].variant,
        ),
    );

    if (isDataFound) return;

    let removeItemFromCart = window.dataLayer.find((data: any) => data.event === 'removeFromCart');

    if (removeItemFromCart) {
      removeItemFromCart.ecommerce.remove.products.push(removedItem.ecommerce.remove.products[0]);
    } else {
      this.initAnalytics();
      this.analytics.once('pageEnd', () => {
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push(removedItem);
        }
      });
    }
    */
    this.initAnalytics();
    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(removedItem);
      }
    });
    /*
    window.dataLayer = window.dataLayer.map((data: any) => {
      if (data.cartContent) {
        return {
          ...data,
          cartContent: data.cartContent.filter(
            (item: any) => item.variant !== removedItem.ecommerce.remove.products[0].variant,
          ),
        };
      }
      return data;
    });
    */
  }

  public pushUserData(userData: any) {
    this.initAnalytics();

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }

    window.dataLayer.push(userData);
  }

  public setCartDataLayer(dataLayerData: Datalayer) {
    const cartDimensions = this.transformCartObjToDataLayer(dataLayerData);
    const cartItems = this.transformCartObjToEmarsysLayer();

    if (typeof window !== 'undefined' && window.dataLayer) {
      const isDataFound = window.dataLayer.some((data: any) => {
        data && isEqual(data, cartDimensions);
      });

      if (isDataFound) return;
    }

    if (this.cartData?.lineItems?.length !== 0) {
      this.initAnalytics();
      this.analytics.once('pageEnd', () => {
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push(cartDimensions);
          // window.dataLayer.push({ cartContent: cartItems });
        }
      });
    }
  }

  public pushCartContentToDataLayer() {
    const cartItems = this.transformCartObjToEmarsysLayer();
    this.initAnalytics();
    // this.analytics.once('pageEnd', () => {
    //   if (typeof window !== 'undefined' && window.dataLayer) {
    //     window.dataLayer.push({ cartContent: cartItems });
    //   }
    // });
  }
}

interface LocaleProps {
  country: string;
  currency: string;
}

/**
 * @param {string} route - current page route
 * @param {LocaleProps} localeData - page data
 * @param {Account | undefined} account - user accound data
 */
export class DataLayerOnLoad {
  private siteURL;
  private route;
  private localeData;
  private account;
  private analytics: any;

  constructor(siteURL: string, route: string, localeData: LocaleProps, account: Account | undefined) {
    this.siteURL = siteURL;
    this.route = route;
    this.localeData = localeData;
    this.account = account;
  }

  private dataMapper() {
    const account = this.account;
    const localeData = this.localeData;
    const mappedData = {
      currencyCode: localeData?.currency || '',
      country: localeData?.country || '',
      userType: account?.accountId ? 'Registered' : 'Guest',
      brandstakId: account?.externalId || '',
      userEmail: account?.email || '',
      breadcrumb: '',
      dimension1: account?.accountId || '',
      dimension2: account?.accountId ? 'login-yes' : 'login-no',
      dimension3: 'loyalty-no',
      dimension13: account?.title || '',
      dimension14: Dimension14Mapper[this.route],
    };

    return mappedData;
  }

  private initAnalytics = () => {
    const currentUrl = document.location.origin;
    this.analytics = Analytics({
      app: 'agstore',
      plugins: [
        googleTagManager({
          containerId: getGTMID(currentUrl),
        }),
      ],
    });
    this.analytics.page();
  };

  setDataLayerOnLoad() {
    this.initAnalytics();
    this.analytics.once('pageEnd', () => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(this.dataMapper());
      }
    });
  }
}
